import React, { useMemo, useState } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  getIdToken,
  deleteUser // Import deleteUser
} from "firebase/auth";
import { auth } from '../firebase'; // Import the auth instance
import { useAuth } from '../contexts/AuthContext'; // Import useAuth hook
import { setupInstitution, acceptInvitation } from '../services/apiService'; // Import the setupInstitution function
import { useLocation, useNavigate } from 'react-router-dom';
import Button from './ui/Button';
import Card from './ui/Card';
import Input from './ui/Input';

/**
 * Component for the Login Screen, handling both Login and Sign Up.
 */
function LoginScreen({ onLoginSuccess, onSignupAndProfileSetupComplete }) {
  const location = useLocation();
  const navigate = useNavigate();
  const invitationToken = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search);
      return params.get('token') || '';
    } catch { return ''; }
  }, [location.search]);
  const { startSignup, completeSignup, cancelSignup } = useAuth(); // Get signup management functions
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState(''); // For signup
  const [institutionName, setInstitutionName] = useState(''); // For signup
  const [isLoginMode, setIsLoginMode] = useState(true); // Toggle between Login and Sign Up
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Function to call the backend setup endpoint
  const handleBackendSetup = async (firebaseUser) => {
    if (!firebaseUser) {
      setError("Signup succeeded but failed to get user info for backend setup.");
      return false; // Indicate setup failure
    }

    console.log('[handleBackendSetup] Starting backend setup process...'); // Log start

    let token;
    try {
      token = await getIdToken(firebaseUser); // Get the Firebase ID Token
      console.log("[handleBackendSetup] Obtained Firebase ID Token."); // Log token success
    } catch (tokenError) {
      console.error("[handleBackendSetup] Error getting Firebase ID token:", tokenError);
      setError("Failed to verify signup session. Please try again.");
      // Attempt to delete the Firebase user if token generation fails
      try {
        await deleteUser(firebaseUser);
        console.warn("[handleBackendSetup] Firebase user deleted due to token generation failure during setup.");
      } catch (deleteErr) {
        console.error("[handleBackendSetup] Failed to delete Firebase user after token error:", deleteErr);
        setError("Signup session error. Please contact support if the problem persists.");
      }
      return false; // Indicate setup failure
    }

    // If the user arrived via an invitation, accept it instead of creating a new institution
    if (invitationToken) {
      console.log('[handleBackendSetup] Accepting invitation via token');
      try {
        await acceptInvitation(invitationToken, token);
        return true;
      } catch (acceptErr) {
        console.error('[handleBackendSetup] Invitation acceptance failed:', acceptErr);
        setError(acceptErr.message || 'Failed to accept invitation.');
        // If invite acceptance failed, delete the Firebase user to avoid orphan accounts
        try { await deleteUser(firebaseUser); } catch { }
        return false;
      }
    }

    console.log('[handleBackendSetup] Calling setupInstitution API service function');

    try {
      const data = await setupInstitution(institutionName, firebaseUser.email, token);
      console.log("[handleBackendSetup] Backend setup successful:", data); // Log success data
      return true; // Indicate setup success

    } catch (setupError) {
      console.error("[handleBackendSetup] Setup API call failed:", setupError);

      let errorMessage = `Backend setup failed: ${setupError.message}`;

      // Handle specific error cases
      if (setupError.status === 409) {
        errorMessage = `Setup failed: ${setupError.message || 'Institution or user might already exist.'}`;
      }

      setError(errorMessage);

      // --- CRITICAL: Attempt to delete the Firebase user ---
      try {
        console.warn("[handleBackendSetup] Attempting to delete Firebase user due to backend setup failure...");
        await deleteUser(firebaseUser);
        console.warn("[handleBackendSetup] Firebase user deleted successfully.");
      } catch (deleteErr) {
        console.error("[handleBackendSetup] Failed to delete Firebase user after backend setup failure:", deleteErr);
        // Add info for the user that cleanup failed
        setError(`${errorMessage} (Automatic cleanup failed, please contact support if you cannot log in)`);
      }
      // --- End Firebase user deletion attempt ---

      return false; // Indicate setup failure
    }
  };


  // Handles the login form submission
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log("Firebase Login Successful:", userCredential.user);
      // No need to call onLoginSuccess here, App.jsx's onAuthStateChanged will handle it
    } catch (firebaseError) {
      console.error("Firebase Login Error:", firebaseError);
      setError(firebaseError.message || 'Failed to login.'); // Show Firebase error message
    } finally {
      setIsLoading(false);
    }
  };

  // Handles the signup form submission
  const handleSignup = async (e) => {
    e.preventDefault();
    console.log("[handleSignup] Starting signup process");

    // Input validation (unchanged)
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    // If this is an invitation-based signup, we don't require an institution name
    if (!invitationToken) {
      if (!institutionName.trim()) {
        setError("Institution name is required.");
        return;
      }
    }
    setError('');
    setIsLoading(true);

    let firebaseUser = null; // Keep track of the created user for potential rollback

    try {
      // 1. Start signup process - this blocks auth state changes
      console.log("[handleSignup] Calling startSignup()");
      startSignup();
      console.log("[handleSignup] startSignup() completed");

      try {
        // 2. Create user in Firebase Auth
        console.log("[handleSignup] Calling createUserWithEmailAndPassword()");
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        firebaseUser = userCredential.user; // Assign the created user
        console.log("[handleSignup] Firebase Signup Successful:", firebaseUser.uid);

        // 3. Call backend setup endpoint or accept invitation
        console.log("[handleSignup] Calling handleBackendSetup()");
        const setupSuccess = await handleBackendSetup(firebaseUser);
        console.log("[handleSignup] handleBackendSetup() returned:", setupSuccess);

        if (setupSuccess) {
          console.log("[handleSignup] User created and backend setup complete.");

          // 4. Complete signup - this manually processes auth state
          console.log("[handleSignup] Calling completeSignup()");
          await completeSignup(firebaseUser);
          console.log("[handleSignup] completeSignup() completed");

          if (onSignupAndProfileSetupComplete) {
            console.log("[handleSignup] Calling onSignupAndProfileSetupComplete()");
            onSignupAndProfileSetupComplete(); // Call the callback
          }
          // User state will now be set and navigation will happen
        } else {
          // 5. Cancel signup on failure - this resumes normal auth state changes
          console.log("[handleSignup] Setup failed, calling cancelSignup()");
          cancelSignup();
          // Error is already set by handleBackendSetup
          // Firebase user *should* have been deleted by handleBackendSetup
          console.error("[handleSignup] Backend setup failed after Firebase signup. Rollback attempted.");
          // Keep isLoading false, error is displayed
        }

      } catch (firebaseError) {
        console.error("[handleSignup] Firebase Signup Error:", firebaseError);

        // Cancel signup on Firebase error
        console.log("[handleSignup] Firebase error, calling cancelSignup()");
        cancelSignup();

        // Handle specific Firebase errors (e.g., email-already-in-use)
        if (firebaseError.code === 'auth/email-already-in-use') {
          setError("This email address is already registered. Please log in or use a different email.");
        } else {
          setError(firebaseError.message || 'Failed to sign up.'); // Show Firebase error message
        }
        // If Firebase creation failed, no need to delete user
        firebaseUser = null;
      }
    } catch (unexpectedError) {
      console.error("[handleSignup] Unexpected error during signup:", unexpectedError);
      // Ensure signup state is always reset on any unexpected error
      console.log("[handleSignup] Unexpected error, calling cancelSignup()");
      cancelSignup();
      setError("An unexpected error occurred during signup. Please try again.");
    } finally {
      console.log("[handleSignup] Setting isLoading to false");
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <Card className="w-full max-w-md">
        <div className="p-8">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
            {isLoginMode ? 'Schedule Login' : 'Create Account'}
          </h2>
          <form onSubmit={isLoginMode ? handleLogin : handleSignup}>
            {/* Email Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="email">Email</label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                required
              />
            </div>

            {/* Password Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="password">Password</label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                required
                minLength={6} // Add basic password length validation
              />
            </div>

            {/* Signup Specific Fields */}
            {!isLoginMode && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="confirmPassword">Confirm Password</label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>
                {!invitationToken && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="institutionName">Institution Name</label>
                    <Input
                      id="institutionName"
                      type="text"
                      placeholder="Enter your institution's name"
                      value={institutionName}
                      onChange={(e) => setInstitutionName(e.target.value)}
                      disabled={isLoading}
                      required={!invitationToken}
                    />
                  </div>
                )}
              </>
            )}

            {/* Error Message Display */}
            {error && <p className="text-red-500 text-sm mb-4 text-center">{error}</p>}

            {/* Submit Button */}
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (isLoginMode ? 'Logging in...' : 'Creating Account...') : (isLoginMode ? 'Login' : 'Sign Up')}
            </Button>
          </form>

          {/* Toggle Mode Link */}
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => { setIsLoginMode(!isLoginMode); setError(''); }} // Clear error on mode switch
              className="text-sm text-blue-600 hover:underline"
              disabled={isLoading}
            >
              {isLoginMode ? 'Need an account? Sign Up' : 'Already have an account? Login'}
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default LoginScreen;
